import { AnalyticPeriodsEnum } from '@workalizer/shared';
import { AnalyticsEntityGroupByEnum, ApplicationEnum, ApplicationGoogleEnum } from '@workalizer/shared/enums';
import { groupByPeriod } from '@workalizer/shared/utils';
import { formatISO } from 'date-fns';
var EVENT_BASED_ACTIVITY_TYPES = new Set([
    ApplicationGoogleEnum.gmail,
    ApplicationGoogleEnum.drive,
    ApplicationGoogleEnum.chat,
    ApplicationGoogleEnum.gemini
]);
var TIME_SPENT_BY_ACTIVITY_TYPE_PERIOD_KEYS = [
    ApplicationEnum.GMAIL,
    ApplicationEnum.DRIVE,
    ApplicationEnum.CHAT,
    ApplicationEnum.MEET,
    ApplicationEnum.CALENDAR,
    ApplicationEnum.GEMINI
];
var emptyEventTotals = function () {
    var _a;
    return (_a = {},
        _a[ApplicationGoogleEnum.gmail] = 0,
        _a[ApplicationGoogleEnum.drive] = 0,
        _a[ApplicationGoogleEnum.chat] = 0,
        _a[ApplicationGoogleEnum.gemini] = 0,
        _a);
};
var sumDedupedDurations = function (items, type) {
    var _a;
    var seen = new Set();
    var total = 0;
    for (var _i = 0, items_1 = items; _i < items_1.length; _i++) {
        var item = items_1[_i];
        if (item.type !== type || item.dedupeKey == null)
            continue;
        if (seen.has(item.dedupeKey))
            continue;
        seen.add(item.dedupeKey);
        total += (_a = item.dedupedDurationSeconds) !== null && _a !== void 0 ? _a : item.durationSeconds;
    }
    return total;
};
export var sumTimeSpentByActivityTypePeriodTotal = function (period) {
    return TIME_SPENT_BY_ACTIVITY_TYPE_PERIOD_KEYS.reduce(function (sum, key) { return sum + period[key]; }, 0);
};
export var emptyTimeSpentByActivityTypePeriod = function (name) {
    var _a;
    if (name === void 0) { name = ''; }
    return (_a = {
            name: name
        },
        _a[ApplicationEnum.GMAIL] = 0,
        _a[ApplicationEnum.DRIVE] = 0,
        _a[ApplicationEnum.CHAT] = 0,
        _a[ApplicationEnum.MEET] = 0,
        _a[ApplicationEnum.CALENDAR] = 0,
        _a[ApplicationEnum.GEMINI] = 0,
        _a);
};
export var sumTimeSpentByActivityTypePeriods = function (periods) {
    return periods.reduce(function (acc, period) {
        var next = emptyTimeSpentByActivityTypePeriod(acc.name || period.name);
        for (var _i = 0, TIME_SPENT_BY_ACTIVITY_TYPE_PERIOD_KEYS_1 = TIME_SPENT_BY_ACTIVITY_TYPE_PERIOD_KEYS; _i < TIME_SPENT_BY_ACTIVITY_TYPE_PERIOD_KEYS_1.length; _i++) {
            var key = TIME_SPENT_BY_ACTIVITY_TYPE_PERIOD_KEYS_1[_i];
            next[key] = acc[key] + period[key];
        }
        return next;
    }, emptyTimeSpentByActivityTypePeriod());
};
export var averageTimeSpentByActivityTypePeriods = function (periods, teamSize) {
    var divisor = Math.max(teamSize, 1);
    return periods.map(function (period) { return ({
        name: period.name,
        GMAIL: Math.round(period.GMAIL / divisor),
        DRIVE: Math.round(period.DRIVE / divisor),
        CHAT: Math.round(period.CHAT / divisor),
        MEET: Math.round(period.MEET / divisor),
        CALENDAR: Math.round(period.CALENDAR / divisor),
        GEMINI: Math.round(period.GEMINI / divisor)
    }); });
};
var mapRowsToPeriodItems = function (rows, groupBy) {
    return groupBy === AnalyticPeriodsEnum.HOUR
        ? rows.map(function (row) { return ({
            items: [row],
            date: formatISO(row.date)
        }); })
        : rows.map(function (row) { return ({
            items: [row],
            date: formatISO(row.date, { representation: 'date' })
        }); });
};
export var buildTimeSpentByActivityTypePeriodsForUser = function (rows, dateFrom, dateTo, groupBy) {
    var periods = groupByPeriod(mapRowsToPeriodItems(rows, groupBy), dateFrom, dateTo, groupBy);
    return periods.map(function (period) { return aggregateByType(period.date, period.items); });
};
export var averageTimeSpentByActivityTypePeriodsAcrossUsers = function (userPeriods, teamSize) {
    var _a, _b;
    if (userPeriods.length === 0 || teamSize === 0) {
        return [];
    }
    var periodCount = (_b = (_a = userPeriods[0]) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0;
    return Array.from({ length: periodCount }, function (_, index) {
        var _a, _b, _c;
        var summed = {
            name: (_c = (_b = (_a = userPeriods[0]) === null || _a === void 0 ? void 0 : _a[index]) === null || _b === void 0 ? void 0 : _b.name) !== null && _c !== void 0 ? _c : '',
            GMAIL: 0,
            DRIVE: 0,
            CHAT: 0,
            MEET: 0,
            CALENDAR: 0,
            GEMINI: 0
        };
        for (var _i = 0, userPeriods_1 = userPeriods; _i < userPeriods_1.length; _i++) {
            var periods = userPeriods_1[_i];
            var period = periods[index];
            if (!period) {
                continue;
            }
            for (var _d = 0, TIME_SPENT_BY_ACTIVITY_TYPE_PERIOD_KEYS_2 = TIME_SPENT_BY_ACTIVITY_TYPE_PERIOD_KEYS; _d < TIME_SPENT_BY_ACTIVITY_TYPE_PERIOD_KEYS_2.length; _d++) {
                var key = TIME_SPENT_BY_ACTIVITY_TYPE_PERIOD_KEYS_2[_d];
                summed[key] += period[key];
            }
        }
        return {
            name: summed.name,
            GMAIL: Math.round(summed.GMAIL / teamSize),
            DRIVE: Math.round(summed.DRIVE / teamSize),
            CHAT: Math.round(summed.CHAT / teamSize),
            MEET: Math.round(summed.MEET / teamSize),
            CALENDAR: Math.round(summed.CALENDAR / teamSize),
            GEMINI: Math.round(summed.GEMINI / teamSize)
        };
    });
};
export var buildTeamAverageTimeSpentByActivityTypePeriods = function (rows, dateFrom, dateTo, groupBy, teamUserIds) {
    if (teamUserIds.length === 0) {
        return [];
    }
    var itemsByUserId = buildItemsByUserId(rows);
    var userPeriods = teamUserIds.map(function (teamUserId) {
        var _a;
        return buildTimeSpentByActivityTypePeriodsForUser((_a = itemsByUserId.get(teamUserId)) !== null && _a !== void 0 ? _a : [], dateFrom, dateTo, groupBy);
    });
    return averageTimeSpentByActivityTypePeriodsAcrossUsers(userPeriods, teamUserIds.length);
};
export var sumTimeSpentOnWorkActivitiesByEntityPeriodTotal = function (byEntity, periodDate) {
    return byEntity.reduce(function (sum, entity) {
        var _a;
        var activity = entity.activities.find(function (a) { return a.date === periodDate; });
        return sum + ((_a = activity === null || activity === void 0 ? void 0 : activity.duration) !== null && _a !== void 0 ? _a : 0);
    }, 0);
};
var aggregateByType = function (periodDate, items) {
    var _a;
    var eventTotals = emptyEventTotals();
    for (var _i = 0, items_2 = items; _i < items_2.length; _i++) {
        var item = items_2[_i];
        if (EVENT_BASED_ACTIVITY_TYPES.has(item.type)) {
            eventTotals[item.type] += item.durationSeconds;
        }
    }
    return _a = {
            name: periodDate
        },
        _a[ApplicationEnum.GMAIL] = eventTotals[ApplicationGoogleEnum.gmail],
        _a[ApplicationEnum.DRIVE] = eventTotals[ApplicationGoogleEnum.drive],
        _a[ApplicationEnum.CHAT] = eventTotals[ApplicationGoogleEnum.chat],
        _a[ApplicationEnum.MEET] = sumDedupedDurations(items, ApplicationGoogleEnum.meet),
        _a[ApplicationEnum.CALENDAR] = sumDedupedDurations(items, ApplicationGoogleEnum.calendar),
        _a[ApplicationEnum.GEMINI] = eventTotals[ApplicationGoogleEnum.gemini],
        _a;
};
/**
 * Shared meetings are deduped inside `aggregateByType`. Summing the team in one
 * pass would count that meeting once, then dividing by headcount understates
 * each attendee. Aggregate each person first so every attendee keeps their time.
 */
export var sumTimeSpentByActivityTypeForUsers = function (rows, userIds) {
    var rowsByUser = new Map();
    for (var _i = 0, rows_1 = rows; _i < rows_1.length; _i++) {
        var row = rows_1[_i];
        if (!userIds.has(row.userId)) {
            continue;
        }
        var userRows = rowsByUser.get(row.userId);
        if (userRows) {
            userRows.push(row);
        }
        else {
            rowsByUser.set(row.userId, [row]);
        }
    }
    return sumTimeSpentByActivityTypePeriods(Array.from(rowsByUser.values(), function (userRows) { return aggregateByType('', userRows); }));
};
var aggregateOrgUnitDuration = function (items, orgUnitUserIds) {
    var orgItems = items.filter(function (item) { return orgUnitUserIds.has(item.userId); });
    var byType = aggregateByType('', orgItems);
    return TIME_SPENT_BY_ACTIVITY_TYPE_PERIOD_KEYS.reduce(function (sum, key) { return sum + byType[key]; }, 0);
};
var aggregateUserDuration = function (items) {
    var total = 0;
    var seenMeetings = new Set();
    var seenCalendars = new Set();
    for (var _i = 0, items_3 = items; _i < items_3.length; _i++) {
        var item = items_3[_i];
        if (item.type === ApplicationGoogleEnum.meet && item.dedupeKey != null) {
            if (seenMeetings.has(item.dedupeKey))
                continue;
            seenMeetings.add(item.dedupeKey);
            total += item.durationSeconds;
            continue;
        }
        if (item.type === ApplicationGoogleEnum.calendar &&
            item.dedupeKey != null) {
            if (seenCalendars.has(item.dedupeKey))
                continue;
            seenCalendars.add(item.dedupeKey);
            total += item.durationSeconds;
            continue;
        }
        total += item.durationSeconds;
    }
    return total;
};
var buildOrgUnitUserIdsMap = function (usersInOrgUnits) {
    var _a;
    var map = new Map();
    for (var _i = 0, usersInOrgUnits_1 = usersInOrgUnits; _i < usersInOrgUnits_1.length; _i++) {
        var _b = usersInOrgUnits_1[_i], orgUnitId = _b.orgUnitId, userId = _b.userId;
        var members = (_a = map.get(orgUnitId)) !== null && _a !== void 0 ? _a : new Set();
        members.add(userId);
        map.set(orgUnitId, members);
    }
    return map;
};
var buildItemsByUserId = function (items) {
    var map = new Map();
    for (var _i = 0, items_4 = items; _i < items_4.length; _i++) {
        var item = items_4[_i];
        var userItems = map.get(item.userId);
        if (userItems) {
            userItems.push(item);
        }
        else {
            map.set(item.userId, [item]);
        }
    }
    return map;
};
var buildByEntity = function (periods, entityGroupBy, orgUnits, users, usersInOrgUnits) {
    if (entityGroupBy === AnalyticsEntityGroupByEnum.orgUnit) {
        var orgUnitUserIdsMap_1 = buildOrgUnitUserIdsMap(usersInOrgUnits);
        return orgUnits.map(function (orgUnit) {
            var _a;
            var orgUnitUserIds = (_a = orgUnitUserIdsMap_1.get(orgUnit.id)) !== null && _a !== void 0 ? _a : new Set();
            var activities = periods.map(function (period) { return ({
                date: period.date,
                duration: aggregateOrgUnitDuration(period.items, orgUnitUserIds)
            }); });
            return { id: orgUnit.id, title: orgUnit.title, activities: activities };
        });
    }
    var periodItemsByUser = periods.map(function (period) {
        return buildItemsByUserId(period.items);
    });
    return users
        .map(function (user) {
        var activities = periods.map(function (period, index) {
            var _a, _b;
            return ({
                date: period.date,
                duration: aggregateUserDuration((_b = (_a = periodItemsByUser[index]) === null || _a === void 0 ? void 0 : _a.get(user.id)) !== null && _b !== void 0 ? _b : [])
            });
        });
        return {
            id: user.id,
            title: user.name || user.email,
            activities: activities
        };
    })
        .filter(function (group) { return group.activities.some(function (a) { return a.duration > 0; }); });
};
export var processTimeSpentOnWorkActivities = function (rows, dateFrom, dateTo, groupBy, entityGroupBy, orgUnits, users, usersInOrgUnits) {
    var mappedData = groupBy === AnalyticPeriodsEnum.HOUR
        ? rows.map(function (row) { return ({
            items: [row],
            date: formatISO(row.date)
        }); })
        : rows.map(function (row) { return ({
            items: [row],
            date: formatISO(row.date, { representation: 'date' })
        }); });
    var periods = groupByPeriod(mappedData, dateFrom, dateTo, groupBy);
    return {
        byType: periods.map(function (period) { return aggregateByType(period.date, period.items); }),
        byEntity: buildByEntity(periods, entityGroupBy, orgUnits, users, usersInOrgUnits)
    };
};
